import React, { useState } from 'react';
import { EmptyData } from '@components/molecules/EmptyData';
import { ProjectFeed } from '../projects/components/ProjectFeed';
import { useProjectFeedByOrganization } from '../projects/components/ProjectFeed/hooks';
import { useRememberedSelection } from '@hooks/useRememberedSelection';
import { useSubcontractorData } from './hooks/useSubcontractorData';
import { useSubcontractorRoster } from '@hooks/api/subcontractor/useSubcontractorRoster';
import { useSubcontractorOverview } from '@hooks/api/subcontractor/useSubcontractorOverview';
import { useSubcontractorBids } from '@hooks/api/subcontractor/useSubcontractorBids';
import { useProjectOptions } from './hooks/useProjectOptions';
import { useProjectSelection } from './hooks/useProjectSelection';
import { ProjectSelection } from './components/ProjectSelection';
import { ErrorDisplay } from './components/ErrorBoundary';
import { SubcontractorHeader } from './components/SubcontractorHeader';
import { AttentionBadge } from './components/AttentionBadge';
import { OverviewTab } from './components/OverviewTab';
import { WorkTab } from './components/WorkTab';
import { BidsTab } from './components/BidsTab';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { Loading } from '@components/atoms/Loading';
import { SecondarySidebar } from '@components/organisms/SecondarySidebar';
import { Tab } from '@components/atoms/Tab';
import { Skeleton } from '@components/atoms/Skeleton';
import { useTranslation } from '@hooks/useTranslation';
import { useTask } from '@hooks/api';
import { TaskDetailModal } from '@components/organisms/TaskDetailModal';
import { Project } from '@models/project.model';
import { t } from './utils/format';

type SubcontractorTabId = 'overview' | 'work' | 'bids' | 'activity';

const SubcontractorPage: React.FC = () => {
  const tSub = useTranslation('subcontractors');
  const { subcontractors, isLoading, error, isEmpty } = useSubcontractorData();
  const { data: roster = [], isLoading: rosterLoading } = useSubcontractorRoster();
  const { selectedProjectId, handleProjectChange, resetProjectSelection } = useProjectSelection();
  const [activeTab, setActiveTab] = useState<SubcontractorTabId>('overview');

  // Roster carries the attention-sorted order and health counts; raw subcontractors (with full
  // `projects[]`) still back the project filter, unchanged from before this story.
  const rosterById = new Map(roster.map((entry) => [entry.organizationId, entry]));
  const orderedSubcontractors = [...subcontractors].sort((a, b) => {
    const attentionA = rosterById.get(a.organizationId)?.attention ?? -1;
    const attentionB = rosterById.get(b.organizationId)?.attention ?? -1;
    if (attentionB !== attentionA) return attentionB - attentionA;
    return a.name.localeCompare(b.name);
  });

  const {
    selectedItem: selectedSubcontractor,
    onSelect: onSubcontractorSelect,
  } = useRememberedSelection({
    key: 'subcontractor',
    items: orderedSubcontractors,
    getId: (sub) => sub.organizationId,
    basePath: '/subcontractors',
  });

  const selectedOrganizationId = selectedSubcontractor?.organizationId || null;
  const selectedOrganizationName = selectedSubcontractor?.name;
  const projectOptions = useProjectOptions({
    subcontractors,
    selectedOrganizationId,
  });

  const { activities, paginationActions, totalCount } = useProjectFeedByOrganization({
    organizationId: selectedOrganizationId || '',
    projectId: selectedProjectId.length === 1 ? selectedProjectId[0] : undefined,
    projectIds: selectedProjectId.length > 1 ? selectedProjectId : undefined,
  });

  const {
    data: overview,
    isLoading: overviewLoading,
    error: overviewError,
  } = useSubcontractorOverview(selectedOrganizationId, selectedProjectId.length > 0 ? selectedProjectId : undefined);
  const { data: bids = [], isLoading: bidsLoading } = useSubcontractorBids(
    activeTab === 'bids' ? selectedOrganizationId : null,
  );

  const { fetchTask, task } = useTask();
  const [isTaskDetailModal, setIsTaskDetailModal] = useState(false);
  const handleOpenTask = (taskId: string) => {
    fetchTask(taskId);
    setIsTaskDetailModal(true);
  };
  const taskProject = selectedSubcontractor?.projects.find((p) => p.projectId === task?.projectId);

  const handleSubcontractorSelect = (subcontractor: any) => {
    onSubcontractorSelect(subcontractor.id);
    resetProjectSelection();
    setActiveTab('overview');
  };

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  const tabs = [
    { id: 'overview', name: tSub?.tabOverview || 'Overview' },
    { id: 'work', name: tSub?.tabWork || 'Work' },
    { id: 'bids', name: tSub?.tabBids || 'Bids' },
    { id: 'activity', name: tSub?.tabActivity || 'Activity' },
  ];

  return (
    <div className="flex flex-col h-full">
      <SecondarySidebar
        items={orderedSubcontractors.map((subcontractor) => {
          const entry = rosterById.get(subcontractor.organizationId);
          const subtitle = entry
            ? entry.attention > 0
              ? t(tSub?.rosterLine, '{overdue} overdue · {approvals} approval(s) · {incidents} incident(s)', {
                  overdue: entry.overdueCount,
                  approvals: entry.awaitingApprovalCount,
                  incidents: entry.openIncidentsCount,
                })
              : t(tSub?.rosterAllClear, 'All clear · {n} active', { n: entry.activeTasksCount })
            : undefined;
          return {
            id: subcontractor.organizationId,
            name: subcontractor.name,
            subtitle,
            iconRight: entry ? <AttentionBadge count={entry.attention} /> : undefined,
          };
        })}
        addButtonLabel="New Subcontractor"
        selectedItem={selectedOrganizationId || undefined}
        handleMenuItemClick={handleSubcontractorSelect}
      />

      {(isLoading || rosterLoading) && (
        <div className="flex justify-center items-center h-full">
          <Loading size="xl" />
        </div>
      )}

      {!isLoading && !rosterLoading && isEmpty && (
        <div className="h-full">
          <EmptyData
            description="No Subcontractors Yet"
            textButton="Contact Support"
            onClickButton={() => window.open('mailto:projectsupport@getzenhammer.com')}
            fullHeight
          />
        </div>
      )}

      {!isLoading && !rosterLoading && selectedOrganizationId && (
        <div className="flex flex-col h-[calc(100vh-3rem)] min-h-0">
          <div className="flex items-center gap-2 mb-4 px-8 flex-shrink-0">
            <BusinessOutlinedIcon sx={{ fontSize: 28 }} className="text-orange-400 dark:text-orange-300" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Subcontractors</h1>
          </div>

          {overview && !overviewLoading && <SubcontractorHeader overview={overview} />}
          {overviewLoading && (
            <div className="px-8 mb-4 flex-shrink-0">
              <Skeleton height={90} />
            </div>
          )}

          <div className="flex-shrink-0 px-8">
            <ProjectSelection
              selectedOrganizationName={selectedOrganizationName}
              selectedOrganizationId={selectedOrganizationId || undefined}
              projectOptions={projectOptions}
              selectedProjectId={selectedProjectId.length === 1 ? selectedProjectId[0] : null}
              onProjectChange={handleProjectChange}
            />
          </div>

          <div className="flex-shrink-0 px-8 mt-4">
            <Tab
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={(id) => setActiveTab(id as SubcontractorTabId)}
              variant="underline"
            />
          </div>

          <div className="mt-6 flex-1 min-h-0 overflow-y-auto no-scrollbar px-8">
            {activeTab === 'overview' &&
              (overviewError ? (
                <ErrorDisplay error={overviewError} />
              ) : overviewLoading || !overview ? (
                <Skeleton height={300} />
              ) : overview.projects.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {tSub?.emptyDelegated || 'Nothing delegated yet.'}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {t(tSub?.emptyDelegatedHint, 'Send {sub} a bid request or delegate a task from a project.', {
                      sub: selectedOrganizationName || '',
                    })}
                  </p>
                </div>
              ) : (
                <OverviewTab overview={overview} onOpenTask={handleOpenTask} />
              ))}

            {activeTab === 'work' &&
              (overviewLoading || !overview ? (
                <Skeleton height={300} />
              ) : (
                <WorkTab projects={overview.projects} onOpenTask={handleOpenTask} />
              ))}

            {activeTab === 'bids' &&
              (bidsLoading ? (
                <Skeleton height={200} />
              ) : (
                <BidsTab bids={bids} subOrganizationName={selectedOrganizationName || ''} />
              ))}

            {activeTab === 'activity' && (
              <ProjectFeed activities={activities} totalCount={totalCount} paginationActions={paginationActions} />
            )}
          </div>
        </div>
      )}

      <TaskDetailModal
        task={task}
        isTaskDetailModal={isTaskDetailModal}
        setIsTaskDetailModal={setIsTaskDetailModal}
        project={taskProject as unknown as Project}
      />
    </div>
  );
};

export default SubcontractorPage;
